import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";

const SCRIPT = [
  { t: 200,   m: "› Establishing secure intelligence channel…" },
  { t: 700,   m: "› Authenticating with ChatGPT (GPT-5.2)…" },
  { t: 1400,  m: "› Authenticating with Claude (Sonnet 4.5)…" },
  { t: 2100,  m: "› Authenticating with Gemini (3 Flash)…" },
  { t: 2900,  m: "› Dispatching PROMPT A — Entity Definition" },
  { t: 3800,  m: "› Dispatching PROMPT B — Category Discovery" },
  { t: 4700,  m: "› Dispatching PROMPT C — Competitive Positioning" },
  { t: 5800,  m: "› Receiving model responses…" },
  { t: 7500,  m: "› Cross-referencing entity descriptions across models…" },
  { t: 9500,  m: "› Scoring 5 dimensions against the Entity Agency rubric" },
  { t: 11500, m: "› Synthesizing consensus narrative" },
  { t: 13500, m: "› Compiling visibility gaps and quick wins" },
  { t: 15500, m: "› Finalizing AI Visibility Snapshot™…" },
];

export default function Processing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [status, setStatus] = useState("processing");
  const [errMsg, setErrMsg] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    const timers = SCRIPT.map(({ t, m }) =>
      setTimeout(() => setLines((cur) => [...cur, m]), t)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/audits/${id}`);
        const s = data?.audit?.status;
        setStatus(s);
        if (s === "complete") {
          setTimeout(() => navigate(`/snapshot/${id}`), 900);
          return;
        }
        if (s === "failed") {
          setErrMsg(data?.audit?.error || "Snapshot failed. Please try again.");
          return;
        }
      } catch (e) {}
      attempts++;
      if (attempts < 90) pollRef.current = setTimeout(poll, 3000);
    };
    poll();
    return () => clearTimeout(pollRef.current);
  }, [id, navigate]);

  return (
    <div className="relative min-h-[88vh]" data-testid="processing-page">
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.04] pointer-events-none"
        style={{ background: "radial-gradient(circle at center, #b8963e 0%, transparent 60%)" }}
      />
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 relative">
        <p className="overline">Generating</p>
        <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-[1.05]" style={{fontWeight: 700}}>
          Reading what AI <em className="text-[#b8963e] italic font-normal">thinks</em> of you.
        </h1>
        <p className="mt-4 text-[#c9bca4]">
          This usually takes 60–90 seconds. Please don't close this window.
        </p>

        <div className="mt-12 eav-card p-6 sm:p-7 min-h-[320px] relative" data-testid="terminal-block">
          <div className="absolute top-0 left-0 right-0 h-px gold-rule" />
          <div className="flex items-center gap-2 mb-5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#c46a5a]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#d4a04a]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#6db66d]" />
            <span className="ml-4 font-mono-eav text-xs text-[#5e5448]">entity-agency — analyze</span>
          </div>
          <div className="font-mono-eav text-sm leading-relaxed">
            {lines.map((l, i) => (
              <div key={i}
                   style={{ animationDelay: `${i * 50}ms` }}
                   className="terminal-line text-[#c9bca4]">
                {l}
              </div>
            ))}
            {status !== "complete" && status !== "failed" && (
              <div className="text-[#b8963e] blink-cursor"> </div>
            )}
            {status === "failed" && (
              <div data-testid="processing-error" className="mt-4 text-[#c46a5a]">✗ {errMsg}</div>
            )}
            {status === "complete" && (
              <div data-testid="processing-complete" className="mt-4 text-[#6db66d]">✓ Snapshot ready — redirecting…</div>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
          {["GPT-5.2", "Claude 4.5", "Gemini 3"].map((p) => (
            <div key={p} className="eav-card py-3">
              <p className="font-mono-eav text-xs text-[#c9bca4]">{p}</p>
              <p className="mt-1 font-mono-eav text-[10px] tracking-wider"
                 style={{ color: status === "complete" ? "#6db66d" : "#b8963e" }}>
                {status === "complete" ? "● DONE" : "● RUNNING"}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
